import { memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SettingsIcon from "@mui/icons-material/Settings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const CrmSetupIntroductionStep = memo(() => {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: { xs: 2, sm: 2.5 },
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.75}>
          <Typography variant="h6" fontWeight={700}>
            Apresentação do modelo do CRM Clínico
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Você configura o canal de mensagens, prepara os conteúdos e define
            as regras de automação por etapa do atendimento. Depois, ao ativar
            uma automação, o disparo segue automaticamente no WhatsApp para
            o paciente, conforme o evento escolhido.
            Antes de ativar de forma definitiva, você também tem um passo de
            teste para validar o comportamento do envio em modo simulado.
          </Typography>
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            Fluxo de uso (setup - mensagens)
          </Typography>

          {/* 1) Canal */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "secondary100.main",
                border: "1px solid",
                borderColor: "secondary200.main",
                flexShrink: 0,
              }}
            >
              <FacebookIcon sx={{ fontSize: 22, color: "secondary.main" }} />
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                Canal Meta/WABA - WhatsApp
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Na configuração, você vincula sua conta Meta ao WhatsApp
                Business (WABA). A partir disso, o CRM usa o canal conectado
                para enviar mensagens.
              </Typography>
            </Stack>
          </Stack>

          {/* 2) Conteúdo */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "warning100.main",
                border: "1px solid",
                borderColor: "warning200.main",
                flexShrink: 0,
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 22, color: "warning600.main" }} />
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                Conteúdo: templates + questionários
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Templates definem o texto das mensagens. Questionários descrevem
                o que será usado para montar o contexto do envio nas etapas do
                atendimento.
              </Typography>
            </Stack>
          </Stack>

          {/* 3) Automação */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "secondary100.main",
                border: "1px solid",
                borderColor: "secondary200.main",
                flexShrink: 0,
              }}
            >
              <SettingsIcon sx={{ fontSize: 22, color: "secondary.main" }} />
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                Automação por etapa (evento - intervalo de tempo - conteúdo)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Você escolhe um evento do fluxo, define antes/depois e a janela
                de tempo, seleciona template e questionário e então ativa/edita a
                automação pelo painel.
              </Typography>
            </Stack>
          </Stack>

          {/* 4) Execução */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "success100.main",
                border: "1px solid",
                borderColor: "success200.main",
                flexShrink: 0,
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 22, color: "success600.main" }} />
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                Disparo no WhatsApp (sem ação manual a cada envio)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Depois da ativação, o sistema segue os fluxos definidos e executa
                o disparo pelo canal conectado. Você acompanha o que está em uso
                no painel e pode desabilitar ou editar quando precisar.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
});

CrmSetupIntroductionStep.displayName = "CrmSetupIntroductionStep";

export default CrmSetupIntroductionStep;
